/**
 * Maps each SoundId to its audio file variations.
 *
 * Files live in ./assets and follow the naming convention `<Sound-Name>-<N>.m4a`
 * where `-<N>` is the variation number. Playback picks a variation at random,
 * never repeating the same one twice in a row.
 *
 * To add a sound: add a SoundId, encode the file(s) to .m4a (see README.md), drop them into
 * ./assets and import them here. Other formats (wav/mp3/ogg) also work — see the audio rule in
 * the webpack configs and src/types/audio.d.ts.
 */
import { SoundId } from './soundConstants';

import boardDrop1 from './assets/Board-Drop-1.m4a';
import cardDrop4 from './assets/Card-Drop-4.m4a';
import cardDrop5 from './assets/Card-Drop-5.m4a';
import cardDrop6 from './assets/Card-Drop-6.m4a';
import cardPickup2 from './assets/Card-Pickup-2.m4a';
import cardPickup6 from './assets/Card-Pickup-6.m4a';
import cardPickup9 from './assets/Card-Pickup-9.m4a';
import cardPickup19 from './assets/Card-Pickup-19.m4a';
import cardPickup21 from './assets/Card-Pickup-21.m4a';
import cardPickup22 from './assets/Card-Pickup-22.m4a';
import contract from './assets/Contract-1.m4a';
import deleteSomething from './assets/Delete-Something-1.m4a';
import error from './assets/Error-1.m4a';
import lineConnect1 from './assets/Line-Connect-1.m4a';
import lineConnect4 from './assets/Line-Connect-4.m4a';
import checkboxTick from './assets/Checkbox-Tick-1.m4a';
import popupOpen2 from './assets/Popup-Open-2.m4a';
import reactionAdd2 from './assets/Reaction-Add-2.m4a';
import commentAdd from './assets/Comment-Add-1.m4a';
import remoteUserDeselect from './assets/Remote-User-Deselect-1.m4a';
import remoteUserSelect from './assets/Remote-User-Select-1.m4a';
import doubleRollOver1 from './assets/Double-Roll-Over-1.m4a';
import doubleRollOver2 from './assets/Double-Roll-Over-2.m4a';
import rollOver1 from './assets/Roll-Over-1.m4a';
import rollOver2 from './assets/Roll-Over-2.m4a';
import rollOver5 from './assets/Roll-Over-5.m4a';
import rollOver6 from './assets/Roll-Over-6.m4a';
import switchToggleOff from './assets/Switch-Toggle-Off-1.m4a';
import switchToggleOn from './assets/Switch-Toggle-On-1.m4a';
import taskNewTaskAppear1 from './assets/Task-New-Task-Appear-1.m4a';

/** One audio file (its bundled URL) */
export type SoundFile = { url: string };
export type SoundManifest = Record<SoundId, SoundFile[]>;

const file = (url: string): SoundFile => ({ url });

const TASK_NEW_TASK_APPEAR_FILES: SoundFile[] = [file(taskNewTaskAppear1)];

export const SOUND_MANIFEST: SoundManifest = {
    [SoundId.CardDrop]: [file(cardDrop4), file(cardDrop5), file(cardDrop6)],
    [SoundId.BoardDrop]: [file(boardDrop1)],
    [SoundId.CardPickup]: [
        file(cardPickup2),
        file(cardPickup6),
        file(cardPickup9),
        file(cardPickup19),
        file(cardPickup21),
        file(cardPickup22),
    ],
    [SoundId.Contract]: [file(contract)],
    [SoundId.DeleteSomething]: [file(deleteSomething)],
    [SoundId.Error]: [file(error)],
    [SoundId.LineConnect]: [file(lineConnect1), file(lineConnect4)],
    [SoundId.CheckboxTick]: [file(checkboxTick)],
    // Unticking shares the "new empty task" pool
    [SoundId.CheckboxUntick]: TASK_NEW_TASK_APPEAR_FILES,
    // Popup-Open-2 was originally Card-Drop-1, repurposed for popups
    [SoundId.PopupOpen]: [file(popupOpen2)],
    [SoundId.ReactionAdd]: [file(reactionAdd2)],
    [SoundId.RemoteUserDeselect]: [file(remoteUserDeselect)],
    [SoundId.RemoteUserSelect]: [file(remoteUserSelect)],
    [SoundId.RollOver]: [file(rollOver1), file(rollOver2), file(rollOver5), file(rollOver6)],
    [SoundId.DoubleRollOver]: [file(doubleRollOver1), file(doubleRollOver2)],
    [SoundId.CommentAdd]: [file(commentAdd)],
    [SoundId.SwitchToggleOff]: [file(switchToggleOff)],
    [SoundId.SwitchToggleOn]: [file(switchToggleOn)],
    [SoundId.TaskNewTaskAppear]: TASK_NEW_TASK_APPEAR_FILES,
};
